import React, {useEffect, useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {Box, AutoSuggestSelect, Input, Checkbox, Select} from '@spothero/ui';
import VehicleAPI from 'api/vehicle';
import APIUtils from '@spothero/utils/api';
import ErrorUtils from 'utils/error-utils';
import ReservationUtils from 'utils/reservation-utils';

const NEW_VEHICLE_KEYS = {
    VEHICLE_INFO_ID: 'vehicleInfoId',
    LICENSE_PLATE: 'licensePlateValue',
    LICENSE_PLATE_STATE: 'licensePlateState',
    IS_DEFAULT: 'isDefault',
    UNLISTED_MODEL: 'unlistedModel',
    MAKE_LABEL: 'makeLabel',
    MODEL_LABEL: 'modelLabel',
};

const AddVehicle = ({onChange, errors, hideDefaultCheckbox}) => {
    const [stateOptions, setStateOptions] = useState([]);

    const [vehicleError, setVehicleError] = useState(
        errors?.includes(ReservationUtils.NEW_VEHICLE_ERRORS.NO_VEHICLE)
            ? ReservationUtils.NEW_VEHICLE_ERRORS.NO_VEHICLE
            : null
    );
    const plateRef = useRef();
    const defaultVehicleRef = useRef();

    const getStates = () => {
        return APIUtils.get('states/');
    };

    useEffect(() => {
        getStates()
            .then(response => {
                const [us, canada] = response.data.data.results;

                const unitedStatesOptions = us.states.map(result => ({
                    value: result.name,
                    label: result.value,
                }));

                const canadianOptions = canada.states.map(result => ({
                    value: result.name,
                    label: result.value,
                }));

                const newStateOptions = unitedStatesOptions.concat(
                    canadianOptions
                );

                newStateOptions.sort((a, b) => (a.label > b.label ? 1 : -1));

                setStateOptions(newStateOptions);
            })
            .catch(error => {
                // Hide state select if no state options
                setStateOptions(null);

                ErrorUtils.sendSentryException(error);
            });
    }, []);

    useEffect(() => {
        setVehicleError(
            errors?.includes(ReservationUtils.NEW_VEHICLE_ERRORS.NO_VEHICLE)
                ? ReservationUtils.NEW_VEHICLE_ERRORS.NO_VEHICLE
                : null
        );
    }, [errors]);

    const getVehicles = async term => {
        try {
            const response = await VehicleAPI.getVehicleInfos(term);
            const results = response.data.data.results;
            const size = 10;

            const trimmedResults = results.slice(0, size).map(result => ({
                label: result.display_description,
                value: result.id,
                vehicleData: result,
            }));

            trimmedResults.push({
                label: 'Vehicle Not Listed',
                value: 0,
                vehicleData: {},
            });

            return trimmedResults;
        } catch (error) {
            ErrorUtils.sendSentryException(error);

            return null;
        }
    };

    const handleVehicleChange = vehicle => {
        setVehicleError(null);
        onChange({
            [NEW_VEHICLE_KEYS.VEHICLE_INFO_ID]: vehicle?.value,
            [NEW_VEHICLE_KEYS.UNLISTED_MODEL]: vehicle?.value === 0,
            [NEW_VEHICLE_KEYS.MAKE_LABEL]: vehicle?.vehicleData?.display_make,
            [NEW_VEHICLE_KEYS.MODEL_LABEL]: vehicle?.vehicleData?.display_model,
        });
    };

    const handlePlateChange = () => {
        onChange({[NEW_VEHICLE_KEYS.LICENSE_PLATE]: plateRef.current.value});
    };

    const handlePlateStateChange = e => {
        onChange({
            [NEW_VEHICLE_KEYS.LICENSE_PLATE_STATE]: e.target.value,
        });
    };

    const handleDefaultVehicleChange = () => {
        onChange({
            [NEW_VEHICLE_KEYS.IS_DEFAULT]: defaultVehicleRef.current.checked,
        });
    };

    return (
        <Box data-testid="AddVehicle">
            <Box data-testid="AddVehicle-autosuggest-vehicle">
                <AutoSuggestSelect
                    id="vehicleMakeModel"
                    label="Make and Model"
                    isRequired
                    isInvalid={Boolean(vehicleError)}
                    getOptions={getVehicles}
                    onChange={vehicle => handleVehicleChange(vehicle)}
                    lineHeight="1.4"
                    placeholder="Type to Search"
                    iconSrc="/img/parking-pass/search.svg"
                />
            </Box>
            {vehicleError && (
                <Box color="error" marginTop={1} fontSize="sm">
                    {vehicleError}
                </Box>
            )}
            <Box marginTop={4} marginBottom={4}>
                <Input
                    isRequired
                    label="License Plate"
                    id="vehicleLicensePlate"
                    ref={plateRef}
                    onChange={handlePlateChange}
                    data-testid="AddVehicle-input-plate"
                />
            </Box>
            {stateOptions && (
                <Box marginBottom={4}>
                    <Select
                        id="vehicleState"
                        label="State or Province"
                        placeholder="Select a State or Province"
                        fontSize="base"
                        onChange={handlePlateStateChange}
                        data-testid="AddVehicle-input-state"
                    >
                        {stateOptions.map(stateOption => (
                            <option
                                key={stateOption.value}
                                value={stateOption.value}
                            >
                                {stateOption.label}
                            </option>
                        ))}
                    </Select>
                </Box>
            )}
            {hideDefaultCheckbox ? null : (
                <Checkbox
                    type="checkbox"
                    id="vehicleDefault"
                    width="full"
                    cursor={{
                        base: 'pointer',
                        desktop: 'default',
                    }}
                    ref={defaultVehicleRef}
                    onChange={handleDefaultVehicleChange}
                    data-testid="AddVehicle-checkbox-default"
                >
                    Make this my default vehicle
                </Checkbox>
            )}
        </Box>
    );
};

AddVehicle.propTypes = {
    onChange: PropTypes.func.isRequired,
    errors: PropTypes.array,
    hideDefaultCheckbox: PropTypes.bool.isRequired,
};

export default AddVehicle;
